import { Dayjs } from 'dayjs';
import type { DataListItem } from '@mts/components/Base';
import {
  cloneDeep,
  DAY_FORMAT,
  getFormConfig,
  getRangePresets,
  getTableColumns,
  isStringArray,
  parseRangeRules,
  pick,
} from '@mts-fe/utils';
import { getReadonlyComponent } from '@mts/components/Form/src/helper';
import { getDataIndex } from '@mts/components/Table/src/helper';
import { t } from '@mts/hooks/web/useI18n';
import type {
  ActionBtnItem,
  BaseFormProps,
  BaseFormSchema,
  LIST_PAGE_SCHEMA,
  PAGE_ITEM_SCHEMA,
  PageModal,
  ParseOptions,
  RenderTableColumn,
  RenderTableProps,
  SchemaListPageProps,
  SchemaPageItemProps,
} from '..';
import {
  COMPARATOR,
  createSchemaApi,
  DICT_OPTION_API,
  getActionHookFn,
  getComparator,
  getDictOptions,
} from '..';

/** Schema解析 */
export const parsePageSchema = (
  pageSchema: LIST_PAGE_SCHEMA,
  options: ParseOptions = {},
): SchemaListPageProps => {
  const { title, api, search, table, headerBtns, modals, ...restProps } = cloneDeep(pageSchema);

  return {
    ...restProps,
    useSearchForm: !!search,
    formConfig: search ? parseBaseSearch(search, modals) : search,
    ...parseBaseTable({ ...table, api: createSchemaApi(api) }, options),
    headerBtns: parseActionBtns(headerBtns),
    modals,
  };
};

/** Schema解析 */
export const parsePageItemSchema = (
  pageSchema: PAGE_ITEM_SCHEMA,
  { pageMode }: ParseOptions = {},
): SchemaPageItemProps => {
  const {
    title,
    api,
    mainForm: { schemas: formSchemas, ...formProps },
    headerBtns,
    childTabs,
    modals,
    ...restProps
  } = cloneDeep(pageSchema);

  return {
    ...restProps,
    api: createSchemaApi(api),
    mainForm: getFormConfig({
      ...formProps,
      schemas: parseFormSchemas(formSchemas, { pageMode, modals }),
    }),
    headerBtns: parseActionBtns(headerBtns).reverse(),
    childTabs: childTabs?.map(({ columns, ...tab }) => ({
      ...tab,
      tabName: t(tab.tabName),
      showBatchBar: pageMode === 'Detail' ? false : tab.showBatchBar,
      columns: parseTableColumns(columns, { pageMode, modals }),
    })),
    modals,
  };
};

const POPUP_COMPS = [
  'ApiSelect',
  'ApiSelectAdd',
  'ApiCascader',
  'ApiTreeSelect',
  'DatePicker',
  'RangePicker',
  'QuickRangePicker',
];
const API_COMPS = [
  'ApiSelect',
  'ApiSelectAdd',
  'ApiCascader',
  'ApiSegmented',
  'ApiButtonGroup',
  'ApiRadioGroup',
  'ApiTreeSelect',
  'ModalTableSelect',
  'ReadonlyText',
];
/** 解析表单字段 */
export const parseFormSchemas = (
  schemas: BaseFormSchema[] = [],
  { pageMode, modals = [] }: ParseOptions = {},
): BaseFormSchema[] => {
  // console.log('parseFormSchemas', schemas, pageMode);
  const getModalConfig = (schema: BaseFormSchema) =>
    modals.find((i) => i.modalId === schema.componentProps.modalId);

  let hasDuplicate = false;
  const byField = new Map<string, BaseFormSchema>();

  const parsed = cloneDeep(schemas).map((item) => {
    item.defaultValue = item.defaultValue !== '' ? item.defaultValue : void 0;

    if (pageMode) {
      const mode = pageMode === 'Copy' ? 'Add' : pageMode;
      item.ifShow = item.ifShow || !item['hidden' + mode];
      item.dynamicDisabled =
        mode === 'Detail' ? true : item.dynamicDisabled || item['disabled' + mode];
      item.required = mode === 'Detail' ? false : item.required;

      if (pageMode === 'Detail') {
        if (item.component === 'Textarea') Object.assign(item.componentProps, showTooltipProps);
        item.component = getReadonlyComponent(item.component, item.componentProps.longText);
      }
    }

    if (item.field) {
      const prev = byField.get(item.field);
      if (!prev) {
        byField.set(item.field, item);
      } else {
        hasDuplicate = true;
        if (prev.ifShow === false && item.ifShow !== false) byField.set(item.field, item);
      }
    }

    if (
      POPUP_COMPS.includes(item.component) &&
      item.componentProps.getPopupContainer === 'parentNode'
    ) {
      item.componentProps.getPopupContainer = (triggerNode) => triggerNode.parentNode;
    }

    const api = item.componentProps.api;
    if (API_COMPS.includes(item.component) && api) {
      item.componentProps.api = api === DICT_OPTION_API ? getDictOptions : createSchemaApi(api);
      ['beforeFetch', 'afterFetch'].forEach((key) => {
        item.componentProps[key] &&
          (item.componentProps[key] = getActionHookFn(item.componentProps[key]));
      });
    }

    if (item.component === 'InputCode') {
      const modalConfig = getModalConfig(item);
      item.componentProps = {
        ...item.componentProps,
        modalConfig: { ...modalConfig, modals },
      };
      !modalConfig && console.error('关联弹窗不存在', item, modals);
    }

    if (item.component === 'ModalTableSelect') {
      const modalConfig = getModalConfig(item);
      item.componentProps = {
        readonly: true,
        allowClear: false,
        valueField: item.field,
        ...item.componentProps,
        modalConfig: { ...modalConfig, modals },
      };
      !modalConfig && console.error('关联弹窗不存在', item, modals);
    }

    if (item.component === 'ApiSelectAdd') {
      const modalConfig = getModalConfig(item);
      item.componentProps = {
        allowClear: false,
        ...item.componentProps,
        modalConfig: { ...modalConfig, modals },
      };
      !modalConfig && console.error('关联弹窗不存在', item, modals);
    }

    if (item.componentProps?.disabledDate && typeof item.componentProps.disabledDate === 'string') {
      const rules = parseRangeRules(item.componentProps.disabledDate);
      item.componentProps.disabledDate = (current: Dayjs) => {
        const currentDate = current.startOf('day');
        if (rules.before && currentDate.isBefore(rules.before)) return true;
        if (rules.after && currentDate.isAfter(rules.after)) return true;
        return false;
      };
    }

    if (item.componentProps?.showPresets) {
      const cfg = item.componentProps.showPresets;
      const presets = getRangePresets(t);
      item.componentProps.presets = isStringArray(cfg)
        ? cfg.map((key) => presets.find((i) => i.key === key)).filter(Boolean)
        : presets;
      delete item.componentProps.showPresets;
    }

    item['dictFieldCodes'] &&
      Object.assign(item.componentProps.params.filter, { fieldDataCodes: item['dictFieldCodes'] });

    return item;
  });

  return hasDuplicate
    ? parsed.filter((item) => !item.field || byField.get(item.field) === item)
    : parsed;
};

export const formSchemasToDataList = (
  schemas: BaseFormSchema[] = [],
  formatItem = (i: DataListItem) => i,
) =>
  schemas.map((i) =>
    formatItem({ ...i, langKey: i.label as string, label: t(i.label as string) } as DataListItem),
  );

/** 解析搜索表单 */
export const parseBaseSearch = (searchProps: BaseFormProps, modals?: PageModal[]) =>
  getFormConfig<BaseFormProps>({
    showActionButtonGroup: true,
    transformDateFunc: (date, format) => date?.format?.(format || DAY_FORMAT.D) ?? date,
    ...searchProps,
    schemas: parseFormSchemas(searchProps.schemas, { modals }),
  });

/** 解析表格配置 */
export const parseBaseTable = (props: RenderTableProps, options: ParseOptions = {}) => ({
  ...props,
  api: createSchemaApi(props.api),
  columns: getTableColumns<RenderTableColumn>(parseTableColumns(props.columns, options)).map(
    ({ pivot, ...col }) => ({
      ...col,
      comparator: col.filter ? getComparator(col.comparator, COMPARATOR.IN) : col.filter,
      pivot: pivot ? { ...pivot, type: pivot.slice === 'measure' ? 'number' : void 0 } : pivot,
    }),
  ),
  actionColumn: props.actionColumn,
  actions: props.actionColumn ? parseActionBtns(props.actions) : [],
  showBatchBar: options.pageMode === 'Detail' ? false : props.showBatchBar,
});

/** 解析表格列字段 */
export const parseTableColumns = (
  columns: RenderTableColumn[] = [],
  { pageMode, modals = [] }: ParseOptions = {},
) => {
  const getModalConfig = ({ editComponentProps: _props }: RenderTableColumn) =>
    modals.find((i) => i.modalId === (typeof _props === 'object' && _props.modalId));

  const popoverFields = columns.reduce(
    (acc, cur) =>
      cur.render?.type === 'RenderPopover' && isStringArray(cur.render.popoverFields)
        ? [...acc, ...cur.render.popoverFields]
        : acc,
    [] as string[],
  );
  return cloneDeep(columns).map((item) => {
    item.render?.params &&
      item['componentProps']?.params &&
      Object.assign(item.render.params, item['componentProps'].params);

    const _codes = item['dictFieldCodes'];
    if (_codes) {
      Object.assign(item.filter?.params?.filter || {}, { fieldDataCodes: _codes });
      Object.assign(item.render?.params?.filter || {}, { fieldDataCodes: _codes });
      Object.assign((item.editComponentProps as Recordable)?.params?.filter || {}, {
        fieldDataCodes: _codes,
      });
    }

    if (item.editComponent === 'InputCode') {
      const modalConfig = getModalConfig(item);
      item.editComponentProps = {
        ...item.editComponentProps,
        modalConfig: { ...modalConfig, modals },
      };
      !modalConfig && console.error('关联弹窗不存在', item, modals);
    }
    if (item.editComponent === 'ModalTableSelect') {
      const modalConfig = getModalConfig(item);
      item.editComponentProps = {
        readonly: true,
        allowClear: false,
        valueField: item.dataIndex,
        ...item.editComponentProps,
        modalConfig: { ...modalConfig, modals },
      };
      !modalConfig && console.error('关联弹窗不存在', item, modals);
    }
    if (item.dataIndex && popoverFields.includes(getDataIndex(item.dataIndex))) {
      item.cacheHidden = true;
      item.defaultHidden = true;
    }
    if (item.editComponent) {
      if (item.editComponent === 'ReadonlyNumber') {
        item.render = {
          type: 'RenderNumber',
          ...item['componentProps'],
          ...pick(item.editComponentProps!, readonlyTextProps),
        };
        delete item.editComponent;
      }
      if (item.editComponent === 'TooltipText') {
        item.render = {
          type: 'RenderDict',
          ...showTooltipProps,
          ...item['componentProps'],
          ...pick(item.editComponentProps!, readonlyTextProps),
        };
        delete item.editComponent;
      }
      if (item.editComponent && pageMode === 'Detail') {
        item.render = {
          type: item.render?.type || 'RenderDict',
          ...(item.editComponent === 'Textarea' ? showTooltipProps : null),
          ...pick(item.editComponentProps!, readonlyTextProps),
        };
        delete item.editComponent;
      }
    }
    return item;
  });
};
const showTooltipProps = { showTips: true, truncate: true, emptyText: '' };
const readonlyTextProps = [
  'api',
  'params',
  'options',
  'resultField',
  'labelField',
  'valueField',
  'concatFields',
  'concatBy',
  'showTips',
  'truncate',
  'multiLang',
  'multipleLine',
  'emptyText',
];

const NO_PARSED_EVENT_TYPES = new Set([
  'BASE_EXPORT',
  'EXPORT_FILE',
  'EXPORT_ASYNC',
  'IMPORT_MODAL',
  'IMPORT_ASYNC',
  'APPROVAL_FLOW',
  'TEMPLATE_PRINT',
]);
/** 解析按钮操作项 */
export const parseActionBtns = (actions: ActionBtnItem[] = [], { pageMode }: ParseOptions = {}) =>
  actions.map((item) => ({
    ...item,
    eventApi: NO_PARSED_EVENT_TYPES.has(item.eventType)
      ? item.eventApi
      : createSchemaApi(item.eventApi),
    hidden: item['hidden' + (pageMode || '')],
    disabled: item['disabled' + (pageMode || '')],
  }));
